// @flow
import type { Country } from 'types/country';
import i18n from 'i18n-js';
import React, { Component } from 'react';
import DatePicker from 'react-datepicker';
import { cloneDeep, concat, get, isEmpty } from 'lodash';
import { Button, Form, Grid, Icon } from 'semantic-grab-ui/src';
import { queryStringFromObject, removeUndefinedAndBlankValues } from 'utils/route-utils';
import { getAllowedTargetUserTypesList, getEventTypesList } from 'utils/new-trident-utils';
import { statusList, targetTypeList, deprecatedBookingEventTypes, deprecatedPaymentEventTypes, defaultOwner, ownerQuest } from 'data/trident-campaigns';
import type { TargetsSearchParams } from 'types/trident-campaigns';
import EventDropdown from 'components/form/trident-campaigns/EventDropdown';
import {
  toFormattedDate,
  toMoment,
  DEFAULT_DATETIME_FORMAT as DATE_TIME_FORMAT,
  DEFAULT_TIME_FORMAT as TIME_FORMAT,
  UNIX_SECONDS_FORMAT,
} from 'utils/time-utils';
import { getListRuleTypes } from 'actions/newTridentExtra';
import { connect } from 'react-redux';
import { StoreState } from 'reducers';
import { DEFAULT_COUNTRY_ID } from 'config';
import { hasPermission } from 'utils/user-utils';

type Props = {
  setRef: Function,
  localeSelector: string,
  router: Object,
  pathname: string,
  searchParams: TargetsSearchParams,
  countries: Array<Country>,
  userPermissions: Object,
  listRuleTypes: Object,
  getListRuleTypes: Function,
  enableQuest: boolean
};

class TridentCampaignSearchForm extends Component {
  props: Props;
  constructor(props: Props) {
    super(props);
    this.state = { searchParams: this.processSearchParams(cloneDeep(this.props.searchParams)) };
    this.props.setRef(this);
    this.allowedTargetUserTypes = getAllowedTargetUserTypesList(this.props.userPermissions, targetTypeList, 'read');
  }

  handleSubmit = (event: any) => {
    event.preventDefault();
    const params = removeUndefinedAndBlankValues(this.state.searchParams);
    const queryString = queryStringFromObject(params);
    const path = `${this.props.pathname}?${queryString}`;
    this.props.router.push(path);
  };

  handleUserTypeChange = (event: any, data: Object) => {
    const newUserType = data.value;
    if (get(this.state.searchParams, 'userType') !== newUserType) {
      this.props.getListRuleTypes({ targetUserType: newUserType, owner: get(this.state.searchParams, 'owner') });
    }
    this.handleInputChange(event, data);
  };

  handlerOwnerChange = (event: any, data: Object) => {
    const newOwner = data.value;
    const targetUserType = get(this.state.searchParams, 'userType', '');
    if (get(this.state.searchParams, 'owner') !== newOwner && targetUserType !== '') {
      this.props.getListRuleTypes({ owner: newOwner, targetUserType });
    }
    this.handleInputChange(event, data);
  }

  handleInputChange = (event: any, { name, value }: { name: string, value: string }) => {
    this.setState({
      searchParams: { ...this.state.searchParams, [name]: value },
    });
  };

  processSearchParams = (searchParams: TargetsSearchParams, continuationTokens: Object = {}) => {
    // backend takes a list of user types, so we need conversion
    const userTypes = searchParams.userType ? [searchParams.userType] : [];

    let countryID = DEFAULT_COUNTRY_ID;
    if (searchParams.countryID) {
      countryID = parseInt(searchParams.countryID, 10);
    } else {
      const allowedCountriesIDs = this.props.userPermissions.countryIDs;
      if (!isEmpty(allowedCountriesIDs)) {
        countryID = allowedCountriesIDs[0];
      }
    }

    let owner = searchParams.owner;
    if (!owner) {
      owner = defaultOwner.key;
    }

    const page = searchParams.page || 1;
    const token = get(continuationTokens, `${countryID}.${page}`, '');
    return { ...searchParams, userTypes, countryID, page, token, owner };
  }

  getEventTypeObject = () => {
    const optionAllTypes = { noun: 'All', eventType: '' };

    const selectedUserType = this.state.searchParams.userType;
    if (isEmpty(selectedUserType)) {
      return {
        rootEventTypes: [optionAllTypes],
      };
    }

    const eventTypeObject = getEventTypesList(this.props.listRuleTypes, selectedUserType);
    eventTypeObject.rootEventTypes = concat(eventTypeObject.rootEventTypes, optionAllTypes);
    const oldEventTypeConfigs = this.getOldEventTypes(selectedUserType);
    if (!isEmpty(oldEventTypeConfigs)) {
      eventTypeObject.eventGroups.deprecated = oldEventTypeConfigs;
    }

    return eventTypeObject;
  };

  getOldEventTypes = (targetUserType: String) => {
    if (targetUserType === 'passenger') {
      return concat(deprecatedBookingEventTypes, deprecatedPaymentEventTypes);
    } else if (targetUserType === 'driver') {
      return deprecatedBookingEventTypes;
    }
    return [];
  };

  getOwnerList = () => {
    const l = [
      defaultOwner,
    ];
    if (this.props.enableQuest) {
      l.push(ownerQuest);
    }
    return l;
  };

  render() {
    const { localeSelector, countries } = this.props;
    const { searchParams } = this.state;
    const countriesList = countries.map(country => ({
      key: country.countryID,
      text: country.name,
      value: country.countryID,
    }));

    const statusOptions = concat([{ text: 'All', value: '' }], statusList);

    const userTypeList = concat(this.allowedTargetUserTypes, { text: 'All', value: '' });

    return (
      <Form>
        <Form.Field>
          <Grid>
            <Grid.Column width={4}>
              <Form.Input
                label={i18n.t(`${localeSelector}.fields.campaignCode`)}
                id="campaignCode"
                name="campaignCode"
                type="text"
                value={searchParams.campaignCode}
                onChange={this.handleInputChange}
              />
            </Grid.Column>
            <Grid.Column width={3}>
              <Form.Select
                name="countryID"
                label={i18n.t(`${localeSelector}.fields.country`)}
                options={countriesList}
                value={searchParams.countryID}
                onChange={this.handleInputChange}
              />
            </Grid.Column>
            <Grid.Column width={3}>
              <Form.Select
                label={i18n.t(`${localeSelector}.fields.status`)}
                id="status"
                name="status"
                options={statusOptions}
                value={searchParams.status}
                onChange={this.handleInputChange}
                placeholder={'grab'}
              />
            </Grid.Column>
            <Grid.Column width={3}>
              <Form.Select
                label="User Type"
                id="userType"
                name="userType"
                options={userTypeList}
                value={searchParams.userType}
                onChange={this.handleUserTypeChange}
              />
            </Grid.Column>
            <Grid.Column width={3}>
              <Form.Select
                label="Owner"
                id="owner"
                name="owner"
                options={this.getOwnerList()}
                value={searchParams.owner}
                onChange={this.handlerOwnerChange}
              />
            </Grid.Column>
          </Grid>
          <Grid>
            <Grid.Column width={4}>
              <div className="field">
                <label>Event Type</label>
                <EventDropdown
                  handleEventSelection={this.handleInputChange}
                  name="eventType"
                  selectedEventName={searchParams.eventType}
                  eventTypesObject={this.getEventTypeObject()}
                />
              </div>
            </Grid.Column>
            <Grid.Column width={4}>
              <Form.Input
                name="startTime"
                label={i18n.t(`${localeSelector}.fields.startTime`)}
                control={DatePicker}
                showTimeSelect
                timeIntervals={15}
                dateFormat={DATE_TIME_FORMAT}
                timeFormat={TIME_FORMAT}
                selected={searchParams.startTime && toMoment(searchParams.startTime, UNIX_SECONDS_FORMAT)}
                onChange={date => this.handleInputChange({}, { name: 'startTime', value: toFormattedDate(date, UNIX_SECONDS_FORMAT) })}
              />
            </Grid.Column>
            <Grid.Column width={4}>
              <Form.Input
                name="endTime"
                label={i18n.t(`${localeSelector}.fields.endTime`)}
                control={DatePicker}
                showTimeSelect
                timeIntervals={15}
                dateFormat={DATE_TIME_FORMAT}
                timeFormat={TIME_FORMAT}
                selected={searchParams.endTime && toMoment(searchParams.endTime, UNIX_SECONDS_FORMAT)}
                onChange={date => this.handleInputChange({}, { name: 'endTime', value: toFormattedDate(date, UNIX_SECONDS_FORMAT) })}
              />
            </Grid.Column>
            <Grid.Column width={2}>
              <Button
                color="blue"
                type="submit"
                onClick={this.handleSubmit}
                icon
                labelPosition="left"
                style={{ marginTop: '25px' }}
              >
                <Icon name="filter" />
                {i18n.t('entities.search')}
              </Button>
            </Grid.Column>
          </Grid>
        </Form.Field>
      </Form>
    );
  }
}

function mapStateToProps(state: StoreState, ownProps: Object) {
  return {
    listRuleTypes: get(state, 'newTridents.listRuleTypes', {}),
    enableQuest: hasPermission(ownProps.userPermissions, 'challenge', '*', 'read'),
  };
}

export default connect(mapStateToProps, {
  getListRuleTypes,
})(TridentCampaignSearchForm);
